import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, Mail, Lock, Globe, Clock, Camera, Save, AlertTriangle, Trash2, CheckCircle, ChevronLeft, Shield, Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, Settings as SettingsIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SelectDropdown } from '../components/SelectDropdown';
import { SettingsPanels } from '../components/SettingsPanels';
const LANGUAGE_OPTIONS = [{
  value: 'en',
  label: 'English (US)'
}, {
  value: 'es',
  label: 'Español'
}, {
  value: 'fr',
  label: 'Français'
}, {
  value: 'de',
  label: 'Deutsch'
}, {
  value: 'zh',
  label: '中文 (Chinese)'
}, {
  value: 'ja',
  label: '日本語 (Japanese)'
}];
const TIMEZONE_OPTIONS = [{
  value: 'UTC-8',
  label: 'Pacific Time (US & Canada) (UTC-08:00)'
}, {
  value: 'UTC-7',
  label: 'Mountain Time (US & Canada) (UTC-07:00)'
}, {
  value: 'UTC-6',
  label: 'Central Time (US & Canada) (UTC-06:00)'
}, {
  value: 'UTC-5',
  label: 'Eastern Time (US & Canada) (UTC-05:00)'
}, {
  value: 'UTC+0',
  label: 'Greenwich Mean Time (UTC+00:00)'
}, {
  value: 'UTC+1',
  label: 'Central European Time (UTC+01:00)'
}, {
  value: 'UTC+8',
  label: 'China Standard Time (UTC+08:00)'
}, {
  value: 'UTC+9',
  label: 'Japan Standard Time (UTC+09:00)'
}];
export function ProfilePage() {
  // Personal Info State
  const [firstName, setFirstName] = useState('Linda');
  const [lastName, setLastName] = useState('Taylor');
  const [email] = useState('linda.taylor@example.com'); // Read-only
  const [avatar, setAvatar] = useState<string | null>(null);
  // Password State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Preferences State
  const [language, setLanguage] = useState('en');
  const [timezone, setTimezone] = useState('UTC-5');
  // UI State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success'>('idle');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  // Open the Settings tab when arriving from a settings link
  useEffect(() => {
    const requestedTab = new URLSearchParams(location.search).get('tab');
    if (requestedTab && ['general', 'security', 'preferences', 'settings'].includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [location.search]);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  const handleSave = () => {
    setSaveStatus('saving');
    setTimeout(() => {
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }, 1000);
  };
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatar(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-2380" data-mp="1lucssmm6t6rl">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-2381" data-mp="1qnnyulj6djig">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2382" data-mp="t78hjzwi3w7d">
          <div className="flex items-center h-16" data-id="element-2383" data-mp="t6yt4gr3tx3s">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-2384" data-mp="y50j7lmy6wor">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-2385" data-mp="30tifssaqbub1">
              <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2386" data-mp="2vuyosdha3sbt">
                <Home size={18} data-id="element-2387" data-mp="39n55c0ocg1bs" />
                <span className="hidden sm:inline" data-id="element-2388" data-mp="34u27vhgg47qb">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2389" data-mp="3jj85j0zmxpw3">
                <BarChart3 size={18} data-id="element-2390" data-mp="3ejvk8koewqvm" />
                <span className="hidden sm:inline" data-id="element-2391" data-mp="3sk6c9r1gkbid">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2392" data-mp="3si27l6vqj8h1">
                <FileText size={18} data-id="element-2393" data-mp="1y5djdzrylyu4" />
                <span className="hidden sm:inline" data-id="element-2394" data-mp="1y6f7b4u2qft9">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2395" data-mp="1u5v69q6dqs1u">
                <UserCog size={18} data-id="element-2396" data-mp="1o272dxt5pty0" />
                <span className="hidden sm:inline" data-id="element-2397" data-mp="1o15hz2q93v3m">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-2398" data-mp="1a8zd4csjtajh">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2399" data-mp="1f8bxmqvy65iw">
                <Bell size={20} data-id="element-2400" data-mp="10bb5wndraakf" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2401" data-mp="15bh1ufr26j8b"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2402" data-mp="rcpqh13kzbnv">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2403" data-mp="real0qpwaulq">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2404" data-mp="2lqiavpnd0lan">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-2405" data-mp="2obg8h2m0fzeu">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2406" data-mp="2kb66e11c9t59" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2407" data-mp="2aam1moem26ia">
                    <div className="py-2" data-id="element-2408" data-mp="37ynsph1u3z0u">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2409" data-mp="380gx0poz675o">
                        <Users size={18} className="text-gray-600" data-id="element-2410" data-mp="2y7t1rzdn8b92" />
                        <span className="font-medium text-gray-900" data-id="element-2411" data-mp="2t9hrxoncm0qu">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2412" data-mp="3qqglpbgwlk8k">
                        <Home size={18} className="text-gray-600" data-id="element-2413" data-mp="3t9jwf4kq2w61" />
                        <span className="font-medium text-gray-900" data-id="element-2414" data-mp="3tb4u332c2v84">
                          Learner Hub
                        </span>
                      </button>
                      <Link to="/admin" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2415" data-mp="7129qor65vq2">
                        <UserCog size={18} className="text-gray-600" data-id="element-2416" data-mp="byu0vgjzpdt7" />
                        <span className="font-medium text-gray-900" data-id="element-2417" data-mp="g2i5bqdbrta8">
                          Admin Hub
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-2418" data-mp="psbhotlakcfj"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2419" data-mp="ureoaigx6qpm">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2420" data-mp="zs2bet212nek">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-2421" data-mp="1p29lnng6azmo"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-2422" data-mp="1l2hdhbuoyvhu"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-2423" data-mp="1z1gz0amwqu9s"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2424" data-mp="1u2e5nngaichn">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2425" data-mp="1g34r0s7qfqfs">
        <div data-phoebe-tour="profile" className="mb-8" data-id="element-2426" data-mp="1g4fv6t53b07f">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2427" data-mp="1bb4kggx5pza8">Profile</h1>
          <p className="text-gray-600" data-id="element-2428" data-mp="nlshhysfangd">
            Manage your account information and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-2429" data-mp="imft39lwl8bo">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-3" data-id="element-2430" data-mp="2u916iz9q6ejf">
            <nav className="space-y-1 sticky top-24" data-id="element-2431" data-mp="2z1asy5bmswgy">
              <button onClick={() => setActiveTab('general')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'general' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2432" data-mp="2z2vmbrm7m9d8">
                <User size={18} data-id="element-2433" data-mp="3d0k4usst14f4" />
                General
              </button>
              <button onClick={() => setActiveTab('security')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'security' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2434" data-mp="3i06nxivbdevu">
                <Shield size={18} data-id="element-2435" data-mp="38xnn14b39cc8" />
                Security
              </button>
              <button onClick={() => setActiveTab('preferences')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'preferences' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2436" data-mp="1sjnnhug5nopi">
                <Globe size={18} data-id="element-2437" data-mp="3i4cptk0x4p6n" />
                Preferences
              </button>
              <button onClick={() => setActiveTab('settings')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'settings' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2438" data-mp="34d7wezo58t4r">
                <SettingsIcon size={18} data-id="element-2439" data-mp="2ugv5g9jzgtdp" />
                Settings
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9 space-y-6" data-id="element-2440" data-mp="2levlp9hl3b72">
            {/* General Section */}
            {activeTab === 'general' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-2441" data-mp="2qdgmba0qzww1">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2442" data-mp="oj7jgwmxn0bj">
                  <div className="p-6 border-b border-gray-200" data-id="element-2443" data-mp="tqe4fuu97dk4">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2444" data-mp="ts8oz81m53no">
                      Personal Information
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-2445" data-mp="12r3sudh6q1nt">
                      Update your photo and personal details.
                    </p>
                  </div>

                  <div className="p-6 space-y-8" data-id="element-2446" data-mp="1hi31foi0sq7e">
                    {/* Avatar Upload */}
                    <div className="flex items-center gap-6" data-id="element-2447" data-mp="1cgnh5yno2nq2">
                      <div className="relative group" data-id="element-2448" data-mp="1qfn3gznpiqj2">
                        <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-md" data-id="element-2449" data-mp="1qebxct6sh6os">
                          {avatar ? <img src={avatar} alt="Profile" className="w-full h-full object-cover" data-id="element-2450" data-mp="3s8jl55c4grc5" /> : <span className="text-2xl font-bold text-gray-500" data-id="element-2451" data-mp="3o7hl4madsb94">
                              LT
                            </span>}
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" data-id="element-2452" data-mp="1r86d3tjiif2z">
                            <Camera className="text-white" size={24} data-id="element-2453" data-mp="212ev3nax2pmw" />
                          </div>
                        </div>
                        <input type="file" accept="image/*" onChange={handleAvatarChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-2454" data-mp="2a4o1q232dyv0" />
                      </div>
                      <div data-id="element-2455" data-mp="2f1obamz4hjqv">
                        <h3 className="font-medium text-gray-900" data-id="element-2456" data-mp="2jur1cl4oyb2b">
                          Profile Photo
                        </h3>
                        <p className="text-sm text-gray-500 mb-3" data-id="element-2457" data-mp="2os95ly7rk23z">
                          This will be displayed on your profile.
                        </p>
                        <div className="flex gap-3" data-id="element-2458" data-mp="2xtz4tq7w05cb">
                          <button className="text-sm font-medium text-[#30718D] hover:text-[#265a70] relative" data-id="element-2459" data-mp="2xuie6ijh1leq">
                            Change
                            <input type="file" accept="image/*" onChange={handleAvatarChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-2460" data-mp="i4sb3qwo47zr" />
                          </button>
                          <button onClick={() => setAvatar(null)} className="text-sm font-medium text-red-600 hover:text-red-700" data-id="element-2461" data-mp="87nlti7izweq">
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2462" data-mp="w5316rq5lijv">
                      <div data-id="element-2463" data-mp="w303dlirel4l">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2464" data-mp="r69mwx31lnin">
                          First Name
                        </label>
                        <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" data-id="element-2465" data-mp="1omov8t7qo0hl" />
                      </div>
                      <div data-id="element-2466" data-mp="18fspjfx2jpgx">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2467" data-mp="18bwjwr3bs1s2">
                          Last Name
                        </label>
                        <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" data-id="element-2468" data-mp="zecgbbhzptea" />
                      </div>
                    </div>

                    <div data-id="element-2469" data-mp="1dd2c8nt2pn12">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2470" data-mp="1icf9m2hkmchw">
                        Email Address
                      </label>
                      <div className="relative" data-id="element-2471" data-mp="2617zbh7lno42">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2472" data-mp="2asq1u9p9twr5" />
                        <input type="email" value={email} readOnly className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed" data-id="element-2473" data-mp="3vivasa2qjdfq" />
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-2474" data-mp="86kpjmupzll3">
                        Contact support to change your email address.
                      </p>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-2475" data-mp="3cjhirxokgcrv">
                    <button onClick={handleSave} disabled={saveStatus === 'saving'} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm disabled:opacity-70" data-id="element-2476" data-mp="37m7tqj53a34k">
                      {saveStatus === 'saving' ? <>Saving...</> : saveStatus === 'success' ? <>
                          <CheckCircle size={18} data-id="element-2477" data-mp="10wt6xchfm1ym" />
                          Saved
                        </> : <>
                          <Save size={18} data-id="element-2478" data-mp="3pnlmrq5nd96w" />
                          Save Changes
                        </>}
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Security Section */}
            {activeTab === 'security' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-2479" data-mp="2joxju5w27xc">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2480" data-mp="3ayfqob23vz38">
                  <div className="p-6 border-b border-gray-200" data-id="element-2481" data-mp="3fy23ije0vdbi">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2482" data-mp="3fvpejm9uk2oy">
                      Password & Security
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-2483" data-mp="2n9o5w4quij0h">
                      Manage your password and account security.
                    </p>
                  </div>

                  <div className="p-6 space-y-6" data-id="element-2484" data-mp="2s5v4fcgwws8e">
                    <div data-id="element-2485" data-mp="2kndoanej6f6g">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2486" data-mp="2knx49ykmethu">
                        Current Password
                      </label>
                      <div className="relative" data-id="element-2487" data-mp="2apr69s1xq7i9">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2488" data-mp="2aslnzbtkde7z" />
                        <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Enter current password" data-id="element-2489" data-mp="26ra13e22r73h" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2490" data-mp="1n2rcaxkzdvpb">
                      <div data-id="element-2491" data-mp="1iy1l3m19p5aq">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2492" data-mp="1j1fjykaq2wdf">
                          New Password
                        </label>
                        <div className="relative" data-id="element-2493" data-mp="31xzhte5vqjkn">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2494" data-mp="36w0yxtjqw8th" />
                          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Min. 8 characters" data-id="element-2495" data-mp="2swjgrx0yftt3" />
                        </div>
                      </div>
                      <div data-id="element-2496" data-mp="2nz9l3zmz2lwt">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2497" data-mp="298k76pi4msp9">
                          Confirm New Password
                        </label>
                        <div className="relative" data-id="element-2498" data-mp="2e5akx66glc7i">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2499" data-mp="207c8i6bp7pun" />
                          <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Re-enter new password" data-id="element-2500" data-mp="255nkonpgucg1" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-2501" data-mp="1qj6uk3s4omg3">
                      <h4 className="text-sm font-medium text-blue-900 mb-2" data-id="element-2502" data-mp="24ipuwaep6thu">
                        Password Requirements
                      </h4>
                      <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside" data-id="element-2503" data-mp="1zitx4swlfyaq">
                        <li data-id="element-2504" data-mp="2ebdn0ruqg9a8">Minimum 8 characters long</li>
                        <li data-id="element-2505" data-mp="29bspmlv7o8zm">At least one uppercase letter</li>
                        <li data-id="element-2506" data-mp="2n9zdd96g4slk">At least one number</li>
                        <li data-id="element-2507" data-mp="2nasgm0674zyy">At least one special character</li>
                      </ul>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-2508" data-mp="2utw5i1aot0av">
                    <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-2509" data-mp="2qsargcmf2fz3">
                      Update Password
                    </button>
                  </div>
                </div>

                {/* Danger Zone */}
                <div className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden" data-id="element-2510" data-mp="2c4fxkswoa5kf">
                  <div className="p-6 border-b border-red-100 bg-red-50" data-id="element-2511" data-mp="275mkv2p7milb">
                    <h2 className="text-lg font-bold text-red-900 flex items-center gap-2" data-id="element-2512" data-mp="2ltppo8rino8j">
                      <AlertTriangle size={20} data-id="element-2513" data-mp="2h1zek0kr8wg2" />
                      Danger Zone
                    </h2>
                    <p className="text-sm text-red-700 mt-1" data-id="element-2514" data-mp="1tc523k4cl9wc">
                      Irreversible actions for your account.
                    </p>
                  </div>

                  <div className="p-6 flex items-center justify-between flex-wrap gap-4" data-id="element-2515" data-mp="3vrmrawi8ioo">
                    <div data-id="element-2516" data-mp="3tyddnuibkcz">
                      <h3 className="font-medium text-gray-900" data-id="element-2517" data-mp="8n1c0ae55k01">
                        Delete Account
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-2518" data-mp="3r1sblcn4pwsp">
                        Permanently delete your account and all of your content.
                      </p>
                    </div>
                    <button onClick={() => setShowDeleteModal(true)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium" data-id="element-2519" data-mp="mnc4fhv3n6nb">
                      Delete Account
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Preferences Section */}
            {activeTab === 'preferences' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-2520" data-mp="2yas770kvyr0i">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200" data-id="element-2521" data-mp="18twm944hq989">
                  <div className="p-6 border-b border-gray-200" data-id="element-2522" data-mp="13w4v4cbo6r54">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2523" data-mp="z43cm8nws46n">
                      Preferences
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-2524" data-mp="v2rvkrj3ora2">
                      Customize your platform experience.
                    </p>
                  </div>

                  <div className="p-6 space-y-6" data-id="element-2525" data-mp="l4u8i7cz90j1">
                    <div data-id="element-2526" data-mp="l76myajj0src">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2527" data-mp="g5r1wihcmm8y">
                        Language
                      </label>
                      <SelectDropdown fullWidth ariaLabel="Language" value={language} onChange={setLanguage} options={LANGUAGE_OPTIONS} icon={<Globe size={18} data-id="element-2529" data-mp="2ahxp6fniothm" />} data-id="element-2528" data-mp="c6043uten5ux" />
                    </div>

                    <div data-id="element-2530" data-mp="26jrmpjtpp02i">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2531" data-mp="26gc5aackfexx">
                        Time Zone
                      </label>
                      <SelectDropdown fullWidth scrollMenu ariaLabel="Time zone" value={timezone} onChange={setTimezone} options={TIMEZONE_OPTIONS} icon={<Clock size={18} data-id="element-2533" data-mp="210h8frtckrv" />} data-id="element-2532" data-mp="3ted05fe3kgmd" />
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-2534" data-mp="61kerkgatqej">
                    <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-2535" data-mp="35is4xhyx2ik9">
                      Save Preferences
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Settings Section */}
            {activeTab === 'settings' && <SettingsPanels data-id="element-2536" data-mp="tqv84gvbkiin" />}
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      <AnimatePresence data-id="element-2537" data-mp="yp6g0m220iy8">
        {showDeleteModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteModal(false)} data-id="element-2538" data-mp="x9kh8xnygxhc">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-2539" data-mp="sc2939hrimae">
              <div className="flex items-center gap-3 mb-4" data-id="element-2540" data-mp="1b93id6icn8k5">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-2541" data-mp="1pyj7yj6597y9">
                  <Trash2 size={24} className="text-red-600" data-id="element-2542" data-mp="1l07ttw8tgba8" />
                </div>
                <div data-id="element-2543" data-mp="1yyelgun69f1g">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-2544" data-mp="1yzpoutcpgks1">
                    Delete Account?
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-2545" data-mp="1u1o320nwa7cv">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-6" data-id="element-2546" data-mp="2ijogwjpn7pqh">
                <p className="text-sm text-red-800" data-id="element-2547" data-mp="24lq5nn6mc9f7">
                  Are you sure you want to delete your account? All of your
                  data, including course progress and certificates, will be
                  permanently removed.
                </p>
              </div>

              <div className="flex gap-3" data-id="element-2548" data-mp="1zllsa610but7">
                <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-2549" data-mp="1zng9pad2l4sj">
                  Cancel
                </button>
                <button onClick={() => {
              // Handle delete logic
              setShowDeleteModal(false);
              alert('Account deletion request sent');
            }} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium shadow-sm" data-id="element-2550" data-mp="1bwatuv74qci8">
                  Yes, Delete
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}